import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  ArrowUpRight,
  Check,
  Monitor,
  CloudOff,
  GitCompare,
  History,
  LoaderCircle,
  RotateCcw,
  Send,
  TriangleAlert,
  Undo2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTrail } from '@/app/layout/trail';
import { recordChannel } from '@/app/site/useLiveSite';
import { useSiteScope } from '@/app/site/useSiteScope';
import { AvatarStack } from '@/features/presence/AvatarStack';
import { PromoteDialog } from '@/features/transfer/PromoteDialog';
import { contentTypesGet } from '@/shared/api/generated/content/content';
import type { RecordBody } from '@/shared/api/generated/model';
import {
  recordsDiscard,
  recordsGet,
  recordsPublish,
  recordsRevert,
  recordsSave,
  recordsUnpublish,
  recordsVersion,
  recordsVersions,
} from '@/shared/api/generated/records/records';
import { ApiError } from '@/shared/api/http';
import { useSession } from '@/shared/auth';
import { realtime, useChannel, usePresence } from '@/shared/realtime';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/Dialog';
import { Segmented } from '@/shared/ui/Segmented';
import { toast } from '@/shared/ui/Toast';

import { DiffList } from './DiffList';
import { fromFieldBodies, type FormField } from './formModel';
import { resolve, threeWay, valueAt } from './merge';
import { FormFields } from './FormFields';
import { PreviewPanel } from './PreviewPanel';
import { PreviewFocusContext, reveal, type PreviewFocus } from './previewFocus';
import { SeoPanel } from './SeoPanel';
import { StageActions, StageHistory } from '@/features/stages/StageActions';
import { hasStages } from '@/features/stages/stages';
import { t } from '@/shared/i18n';

import { ShopRecordPanel } from '../ShopPage/ShopRecordPanel';

import styles from './RecordPage.module.scss';

const dateFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const timeFormat = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });

/** Пауза после последней правки до автосохранения. */
const AUTOSAVE_DELAY = 1500;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

/**
 * Редактор записи.
 *
 * Показывает черновик — всегда. То, что сейчас на сайте, это отдельная копия,
 * и путать их нельзя: человек, правящий «страницу», должен понимать, ушли его
 * правки к читателям или нет.
 *
 * Черновик сохраняется сам — через полторы секунды после правки, с проверкой
 * версии. «Не сохранилось» перестаёт существовать как класс, а публикация —
 * отдельное явное действие (docs/research/05-collaboration.md, п. 3).
 */
export function RecordPage() {
  const { siteId, env, recordId } = useParams({ from: '/authed/sites/$siteId/$env/r/$recordId' });
  const scope = useSiteScope();
  // Кнопки — только тем, кому сервер их действие разрешит (матрица ролей).
  const canWrite = scope?.can('content.write') ?? false;
  const canPublish = scope?.can('content.publish') ?? false;
  const queryClient = useQueryClient();

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<'none' | 'history' | 'site' | 'preview'>('none');
  const [focus, setFocus] = useState<PreviewFocus>();

  // Щелчок по полю на сайте — поле показывается в форме (блоки показывают
  // себя сами, см. BlocksInput).
  useEffect(() => {
    if (focus?.field !== undefined && focus.block === undefined) {
      reveal(document.querySelector(`[data-field-key="${CSS.escape(focus.field)}"]`));
    }
  }, [focus]);
  const [promoting, setPromoting] = useState(false);
  // Перенос на прод — с дева и тем, кто пишет на проде: с сужением
  // «только Development» прода нет.
  const canPromote = env === 'dev' && canWrite && scope?.devOnly !== true;
  const [discarding, setDiscarding] = useState(false);

  const [status, setStatus] = useState<SaveStatus>('idle');
  // Адрес записи правится во вкладке SEO и уходит с ближайшим сохранением.
  const [slugDraft, setSlugDraft] = useState<string>();
  const [section, setSection] = useState<'content' | 'seo'>('content');
  const [savedAt, setSavedAt] = useState<Date>();

  // Счётчик правок: сохранение, начатое до очередной правки, не снимает
  // «есть несохранённое» — следом уйдёт ещё одно.
  const edits = useRef(0);
  // Последние значения и версия — для сохранения из таймера, без замыкания
  // на устаревший рендер.
  const latest = useRef<Record<string, unknown>>({});
  const etag = useRef('');

  // Живая запись: кто ещё в ней и что правит; чужое сохранение долетает
  // сразу. Себя в списке не показываем — даже из соседней вкладки.
  const channel = recordChannel(recordId);
  const session = useSession();
  const me = session.status === 'authenticated' ? session.user.id : undefined;
  const others = usePresence(channel).filter((person) => person.user_id !== me);

  const record = useQuery({
    queryKey: ['record', recordId],
    queryFn: async () => {
      const response = await recordsGet(recordId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос записи');
      }

      return response.data;
    },
  });

  const typeID = record.data?.content_type_id;

  const type = useQuery({
    queryKey: ['content-type', typeID],
    enabled: typeID !== undefined,
    queryFn: async () => {
      const response = await contentTypesGet(typeID ?? '');

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос типа');
      }

      return response.data;
    },
  });

  const versions = useQuery({
    queryKey: ['versions', recordId],
    enabled: panel === 'history',
    queryFn: async () => {
      const response = await recordsVersions(recordId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос истории');
      }

      return response.data.items ?? [];
    },
  });

  // Значения с сервера кладутся в форму, когда приходит новая версия записи,
  // и не перезаписывают набранное: иначе фоновое обновление стирало бы правки
  // прямо под руками.
  //
  // Правка состояния прямо в теле рендера — тот самый случай, для которого
  // React её и допускает: состояние подстраивается под изменившиеся данные.
  const [loadedAt, setLoadedAt] = useState<string>();
  // С чего начиналась правка — значения сервера на момент загрузки или
  // последнего своего сохранения. Без него не отличить «я поменял» от
  // «другой поменял».
  const [base, setBase] = useState<Record<string, unknown>>({});
  // Поля, которые оба поменяли по-разному, и их версия значений.
  const [conflict, setConflict] = useState<{ keys: string[]; theirs: Record<string, unknown> }>();
  // Поля, которые другой поменял и которые свелись сами.
  const [mergedTheirs, setMergedTheirs] = useState<string[]>([]);

  if (record.data !== undefined && record.data.updated_at !== loadedAt) {
    const theirs = record.data.data ?? {};

    setLoadedAt(record.data.updated_at);
    setBase(theirs);

    if (!dirty) {
      setValues(theirs);
    } else {
      // Пока я правил, запись сохранил другой: правки разных полей сводятся
      // сами, одного и того же — выбирает человек.
      const result = threeWay(base, values, theirs);

      setValues(result.merged);

      if (result.conflicts.length > 0) {
        setConflict({ keys: result.conflicts, theirs });
        setStatus('conflict');
      } else {
        setMergedTheirs(result.theirs);

        if (status === 'conflict') {
          setStatus('idle');
        }
      }
    }
  }

  useEffect(() => {
    latest.current = values;
  }, [values]);

  useEffect(() => {
    if (record.data !== undefined && record.data.updated_at === loadedAt) {
      etag.current = record.data.etag;
    }
  }, [record.data, loadedAt]);

  const fields = fromFieldBodies(type.data?.fields ?? []);

  // Языки сайта: первый — основной, его значения — в полях записи; остальные
  // — переводы переводимых полей в data._i18n.
  const languages = scope?.site?.languages ?? ['ru'];
  const mainLanguage = languages[0] ?? 'ru';
  const [language, setLanguage] = useState(mainLanguage);
  const translating = language !== mainLanguage && languages.includes(language);
  const fieldName = (key: string) => fields.find((item) => item.key === key)?.name ?? key;

  const refreshAround = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['versions', recordId] });
    void queryClient.invalidateQueries({ queryKey: ['records'] });
  }, [queryClient, recordId]);

  /** Ответ сервера — новая правда о записи: в кеш, в версию, в «загружено». */
  const accept = useCallback(
    (saved: RecordBody) => {
      etag.current = saved.etag;
      setLoadedAt(saved.updated_at);
      setBase(saved.data);
      queryClient.setQueryData(['record', recordId], saved);
      refreshAround();
    },
    [queryClient, recordId, refreshAround],
  );

  // Запись сохранили (здесь, в другой вкладке или другой человек) — свежая
  // версия перечитывается. Нет своих правок — форма обновится сама; есть —
  // их не трогаем и показываем плашку ниже.
  useChannel(channel, (event) => {
    if (event.event === 'changed') {
      void queryClient.invalidateQueries({ queryKey: ['record', recordId] });
      void queryClient.invalidateQueries({ queryKey: ['versions', recordId] });
    }
  });

  const change = (next: (prev: Record<string, unknown>) => Record<string, unknown>) => {
    edits.current += 1;
    setDirty(true);
    setValues(next);

    if (status === 'saved' || status === 'error') {
      setStatus('idle');
    }
  };

  const fieldLabel = (key: string) => {
    if (!key.startsWith('_i18n.')) {
      return fieldName(key);
    }

    const [, lang = '', field = ''] = key.split('.');

    return `${fieldName(field)} · ${lang}`;
  };

  /** Сохранить черновик сейчас. Возвращает ответ сервера или undefined. */
  const saveNow = useCallback(async (): Promise<RecordBody | undefined> => {
    const started = edits.current;

    setStatus('saving');

    try {
      const response = await recordsSave(
        recordId,
        { data: latest.current, ...(slugDraft === undefined ? {} : { slug: slugDraft }) },
        { 'If-Match': etag.current },
      );

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на сохранение');
      }

      accept(response.data);
      setErrors({});
      setSlugDraft(undefined);
      setSavedAt(new Date());
      setStatus('saved');

      if (edits.current === started) {
        setDirty(false);
      }

      return response.data;
    } catch (cause) {
      // Запись успели изменить другие: автосохранение останавливается, чтобы
      // не затирать их правки, а человек решает, чья версия остаётся.
      if (cause instanceof ApiError && cause.status === 412) {
        setStatus('conflict');
        void queryClient.invalidateQueries({ queryKey: ['record', recordId] });

        return undefined;
      }

      if (cause instanceof ApiError && cause.status === 422) {
        setErrors(fieldErrors(cause));
      }

      setStatus('error');

      return undefined;
    }
  }, [accept, queryClient, recordId, slugDraft]);

  // Автосохранение: пауза после последней правки — и в сервер. Пока идёт
  // сохранение или висит конфликт, новое не начинается.
  useEffect(() => {
    if (!dirty || !canWrite || status === 'saving' || status === 'conflict' || status === 'error') {
      return;
    }

    const timer = setTimeout(() => {
      void saveNow();
    }, AUTOSAVE_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [dirty, canWrite, status, values, saveNow]);

  // ⌘S / Ctrl+S — сохранить сейчас, не дожидаясь паузы. Браузерное
  // «сохранить страницу» здесь никому не нужно.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();

        if (dirty && canWrite && status !== 'saving') {
          void saveNow();
        }
      }
    };

    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [dirty, canWrite, status, saveNow]);

  // Ушёл со страницы с несохранённым — браузер переспросит.
  useEffect(() => {
    if (!dirty) {
      return;
    }

    const onLeave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', onLeave);

    return () => {
      window.removeEventListener('beforeunload', onLeave);
    };
  }, [dirty]);

  /** Действие над записью: публикация, снятие, возврат, отмена правок. */
  const act = async (action: () => Promise<{ status: number; data: unknown }>, done: string) => {
    setBusy(true);

    try {
      const response = await action();

      if (response.status === 200) {
        accept(response.data as RecordBody);
        setDirty(false);
        setStatus('idle');
      }

      toast.success(done);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 412) {
        setStatus('conflict');
        void queryClient.invalidateQueries({ queryKey: ['record', recordId] });
      } else if (cause instanceof ApiError && cause.status === 422) {
        setErrors(fieldErrors(cause));
        toast.error('Проверьте поля', {
          description: 'Опубликовать можно, когда обязательные поля заполнены и значения подходят.',
        });
      } else {
        toast.error('Не удалось выполнить действие', {
          description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const publish = () => {
    // Сначала несохранённое — потом публикация: публиковать то, чего сервер
    // ещё не видел, нельзя. Публикуется ровно сохранённая версия.
    void act(async () => {
      if (dirty) {
        const saved = await saveNow();

        if (saved === undefined) {
          throw new Error('черновик не сохранился');
        }
      }

      return recordsPublish(recordId, { 'If-Match': etag.current });
    }, 'Опубликовано');
  };

  // Коллекция и название записи — хвост хлебных крошек: в адресе их нет,
  // а «где я» должно быть видно и без ссылки «назад».
  useTrail([
    ...(type.data === undefined
      ? []
      : [
          // Страница — в разделе «Страницы», заявка — в своей форме, запись —
          // в своей модели.
          type.data.type.slug === 'pages'
            ? { label: t.nav.pages, href: `/sites/${siteId}/${env}/pages` }
            : {
                label: type.data.type.name,
                href:
                  type.data.type.purpose === 'users'
                    ? `/sites/${siteId}/${env}/users`
                    : `/sites/${siteId}/${env}/${type.data.type.purpose === 'form' ? 'forms' : 'models'}/${type.data.type.id}`,
              },
        ]),
    ...(record.data === undefined ? [] : [{ label: record.data.title }]),
  ]);

  if (record.isPending || type.isPending) {
    return <p className={styles['muted']}>Загружаем…</p>;
  }

  if (record.data === undefined) {
    return <p className={styles['muted']}>Запись не найдена.</p>;
  }

  const current = record.data;
  // Заявка формы и профиль посетителя — не содержимое сайта: публикации,
  // предпросмотра и переноса у них нет.
  const content = (type.data?.type.purpose ?? 'content') === 'content';
  const published = current.status === 'published';
  const diverged = published && (current.has_unpublished_changes || dirty);

  return (
    <div className={styles['page']}>
      <header className={styles['head']}>
        <div className={styles['head__main']}>
          <div className={styles['head__title']}>
            <h1 className={styles['title']}>{current.title}</h1>
            <AvatarStack people={others} fieldName={fieldName} />
          </div>

          <div className={styles['state']}>
            {!content ? null : published ? (
              diverged ? (
                <Badge size="sm" tone="warning">
                  на сайте — прежняя версия
                </Badge>
              ) : (
                <Badge size="sm" tone="success">
                  на сайте
                </Badge>
              )
            ) : (
              <Badge size="sm" tone="neutral">
                черновик, не на сайте
              </Badge>
            )}

            {type.data !== undefined && hasStages(type.data.type.workflow) && (
              <StageActions
                record={current}
                workflow={type.data.type.workflow}
                onChanged={(next) => {
                  queryClient.setQueryData(['record', recordId], next);
                }}
              />
            )}

            {canWrite && (
              <SaveState
                status={status}
                dirty={dirty}
                savedAt={savedAt}
                onRetry={() => {
                  void saveNow();
                }}
              />
            )}
          </div>
        </div>

        <div className={styles['head__actions']}>
          <Button
            variant="ghost"
            tone="neutral"
            iconLeft={<History size={15} />}
            aria-pressed={panel === 'history'}
            onClick={() => {
              setPanel((value) => (value === 'history' ? 'none' : 'history'));
            }}
          >
            История
          </Button>

          {content && diverged && (
            <Button
              variant="ghost"
              tone="neutral"
              iconLeft={<GitCompare size={15} />}
              aria-pressed={panel === 'site'}
              onClick={() => {
                setPanel((value) => (value === 'site' ? 'none' : 'site'));
              }}
            >
              Что изменилось
            </Button>
          )}

          {content && type.data?.type.kind !== 'block' && (
            <Button
              variant="ghost"
              tone="neutral"
              iconLeft={<Monitor size={15} />}
              aria-pressed={panel === 'preview'}
              onClick={() => {
                setPanel((value) => (value === 'preview' ? 'none' : 'preview'));
              }}
            >
              Предпросмотр
            </Button>
          )}

          {content && canPromote && (
            <Button
              variant="outline"
              tone="neutral"
              iconLeft={<ArrowUpRight size={15} />}
              disabled={dirty}
              title={dirty ? 'Сначала дождитесь сохранения' : undefined}
              onClick={() => {
                setPromoting(true);
              }}
            >
              На Production
            </Button>
          )}

          {content && published && canPublish && (
            <Button
              variant="outline"
              tone="neutral"
              iconLeft={<CloudOff size={15} />}
              disabled={busy}
              onClick={() => {
                void act(() => recordsUnpublish(recordId), 'Убрано с сайта');
              }}
            >
              Убрать с сайта
            </Button>
          )}

          {content && canPublish && (
            <Button
              iconLeft={<Send size={15} />}
              loading={busy}
              disabled={published && !diverged}
              onClick={publish}
            >
              {published ? 'Обновить на сайте' : 'Опубликовать'}
            </Button>
          )}
        </div>
      </header>

      {conflict !== undefined && (
        <ConflictPanel
          keys={conflict.keys}
          label={fieldLabel}
          mine={values}
          theirs={conflict.theirs}
          onResolve={(takeTheirs) => {
            setValues((prev) => resolve(prev, conflict.theirs, takeTheirs));
            setConflict(undefined);
            setStatus('idle');
          }}
        />
      )}

      {conflict === undefined && mergedTheirs.length > 0 && (
        <div className={styles['merged']} role="status">
          <p>
            Пока вы правили, запись сохранил другой человек: поменялось{' '}
            {mergedTheirs.map(fieldLabel).join(', ')}. Его правки взяты, ваши — на месте.
          </p>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            onClick={() => {
              setMergedTheirs([]);
            }}
          >
            Понятно
          </Button>
        </div>
      )}

      <div
        className={`${styles['layout'] ?? ''} ${panel === 'preview' ? (styles['layout--preview'] ?? '') : ''}`}
      >
        <PreviewFocusContext.Provider value={focus}>
          <div className={styles['form']}>
            <ShopRecordPanel
              recordId={current.id}
              typeId={current.content_type_id}
              shop={scope?.shop}
              canWrite={canWrite}
              canPublish={canPublish}
            />

            {type.data?.type.seo === true && (
              <div className={styles['sections']}>
                <Segmented
                  value={section}
                  aria-label="Раздел записи"
                  onValueChange={(next) => {
                    setSection(next as 'content' | 'seo');
                  }}
                  options={[
                    { value: 'content', label: 'Содержимое' },
                    { value: 'seo', label: 'SEO' },
                  ]}
                />
              </div>
            )}

            {section === 'seo' && type.data?.type.seo === true ? (
              <SeoPanel
                values={seoOf(values)}
                title={current.title}
                slug={slugDraft ?? current.slug ?? ''}
                errors={errors}
                siteID={type.data.type.site_id}
                siteURL={scope?.environment?.url ?? undefined}
                onChange={(key, next) => {
                  change((prev) => ({ ...prev, _seo: { ...seoOf(prev), [key]: next } }));
                }}
                onSlug={(next) => {
                  edits.current += 1;
                  setSlugDraft(next);
                  setDirty(true);
                }}
              />
            ) : fields.length === 0 ? (
              <p className={styles['muted']}>
                У этого типа нет полей. Добавьте их в «Моделях» — тогда запись будет чем заполнить.
              </p>
            ) : (
              <>
                {languages.length > 1 && (
                  <div className={styles['languages']}>
                    <Segmented
                      value={translating ? language : mainLanguage}
                      onValueChange={setLanguage}
                      aria-label="Язык содержимого"
                      options={languages.map((code, index) => ({
                        value: code,
                        label: index === 0 ? `${code} · основной` : code,
                      }))}
                    />
                  </div>
                )}

                {translating ? (
                  <TranslationFields
                    fields={fields}
                    values={values}
                    language={language}
                    errors={errors}
                    siteID={type.data?.type.site_id ?? ''}
                    onChange={(key, next) => {
                      change((prev) => withTranslation(prev, language, key, next));
                    }}
                  />
                ) : (
                  <FormFields
                    fields={fields}
                    values={values}
                    errors={errors}
                    siteID={type.data?.type.site_id ?? ''}
                    onChange={(key, next) => {
                      change((prev) => ({ ...prev, [key]: next }));
                    }}
                    decorate={(field, node) => {
                      const editing = others.filter((person) => person.field === field.key);

                      return (
                        // Фокус внутри поля — метка «правит» у остальных; ушёл —
                        // метка снята. События фокуса всплывают, поэтому одна
                        // обёртка покрывает и составные поля (блоки, группы, медиа).
                        <div
                          className={styles['field']}
                          data-field-key={field.key}
                          onFocus={() => {
                            realtime.focus(channel, field.key);
                          }}
                          onBlur={(event) => {
                            if (!event.currentTarget.contains(event.relatedTarget)) {
                              realtime.focus(channel, '');
                            }
                          }}
                        >
                          {editing.length > 0 && (
                            <span className={styles['field__mark']}>
                              {editing.map((person) => person.name).join(', ')}
                            </span>
                          )}
                          {node}
                        </div>
                      );
                    }}
                  />
                )}
              </>
            )}
          </div>
        </PreviewFocusContext.Provider>

        {panel === 'preview' && (
          <PreviewPanel
            recordID={recordId}
            slug={slugDraft ?? current.slug ?? undefined}
            route={type.data?.type.route ?? ''}
            base={
              scope?.environment?.kind === 'production'
                ? (scope.environment.preview_url ?? scope.environment.url ?? undefined)
                : (scope?.environment?.url ?? undefined)
            }
            values={values}
            locale={translating ? language : undefined}
            onSelect={(selection) => {
              setSection('content');
              setFocus({ ...selection, nonce: Date.now() });
            }}
          />
        )}

        {panel === 'site' && (
          <aside className={styles['history']}>
            <h2 className={styles['history__title']}>Что изменилось</h2>
            <p className={styles['muted']}>
              Черновик против того, что сейчас на сайте. На сайт это уйдёт после «Обновить на
              сайте».
            </p>

            <DiffList
              fields={fields}
              before={current.published_data ?? {}}
              after={values}
              beforeLabel="на сайте"
              afterLabel="в черновике"
            />

            {canWrite && (
              <Button
                variant="outline"
                tone="neutral"
                iconLeft={<Undo2 size={15} />}
                disabled={busy}
                onClick={() => {
                  setDiscarding(true);
                }}
              >
                Отменить правки
              </Button>
            )}
          </aside>
        )}

        {panel === 'history' && (
          <aside className={styles['history']}>
            {type.data !== undefined && hasStages(type.data.type.workflow) && (
              <>
                <h2 className={styles['history__title']}>Статусы</h2>
                <StageHistory recordId={recordId} workflow={type.data.type.workflow} />
              </>
            )}
            <h2 className={styles['history__title']}>История</h2>
            <p className={styles['muted']}>
              Правки одного захода — до 10 минут подряд — одна версия. Возврат не стирает историю:
              он добавляет новую версию с прежними значениями.
            </p>

            {versions.isPending ? (
              <p className={styles['muted']}>Загружаем…</p>
            ) : (
              <ul className={styles['history__list']}>
                {(versions.data ?? []).map((version) => (
                  <VersionRow
                    key={version.id}
                    recordId={recordId}
                    version={version}
                    fields={fields}
                    current={values}
                    disabled={busy || !canPublish}
                    onRevert={() => {
                      void act(
                        () => recordsRevert(recordId, version.id, { 'If-Match': etag.current }),
                        'Значения версии возвращены',
                      );
                    }}
                  />
                ))}
              </ul>
            )}
          </aside>
        )}
      </div>

      <PromoteDialog
        siteID={siteId}
        recordIDs={promoting ? [recordId] : []}
        onClose={() => {
          setPromoting(false);
        }}
      />

      <ConfirmDialog
        open={discarding}
        onOpenChange={setDiscarding}
        title="Отменить неопубликованные правки?"
        description="Черновик вернётся к тому, что сейчас на сайте. Отменённое останется в истории — вернуть его можно оттуда."
        confirmLabel="Отменить правки"
        tone="danger"
        onConfirm={async () => {
          await act(
            () => recordsDiscard(recordId, { 'If-Match': etag.current }),
            'Правки отменены',
          );
          setPanel('none');
        }}
      />
    </div>
  );
}

/**
 * Конфликт: одно и то же поле поменяли оба. Рядом — оба значения, выбор по
 * каждому полю; до выбора автосохранение стоит.
 */
function ConflictPanel({
  keys,
  label,
  mine,
  theirs,
  onResolve,
}: {
  keys: string[];
  label: (key: string) => string;
  mine: Record<string, unknown>;
  theirs: Record<string, unknown>;
  onResolve: (takeTheirs: Record<string, boolean>) => void;
}) {
  const [take, setTake] = useState<Record<string, boolean>>({});

  return (
    <section className={styles['conflict']} aria-label="Одни и те же поля поменяли двое">
      <p className={styles['conflict__lead']}>
        <b>Пока вы правили, эти же поля поменял другой человек.</b> Выберите, что оставить, —
        остальное уже сведено.
      </p>

      <ul className={styles['conflict__list']}>
        {keys.map((key) => (
          <li key={key} className={styles['conflict__item']}>
            <span className={styles['conflict__name']}>{label(key)}</span>
            <Segmented
              value={take[key] === true ? 'theirs' : 'mine'}
              onValueChange={(next) => {
                setTake((prev) => ({ ...prev, [key]: next === 'theirs' }));
              }}
              aria-label={`Чьё значение оставить: ${label(key)}`}
              options={[
                { value: 'mine', label: `Моё: ${short(valueAt(mine, key))}` },
                { value: 'theirs', label: `Их: ${short(valueAt(theirs, key))}` },
              ]}
            />
          </li>
        ))}
      </ul>

      <div className={styles['conflict__actions']}>
        <Button
          size="sm"
          onClick={() => {
            onResolve(take);
          }}
        >
          Готово — продолжить
        </Button>
      </div>
    </section>
  );
}

function short(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'string') {
    const text = value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return text.length > 40 ? `${text.slice(0, 40)}…` : text;
  }

  if (Array.isArray(value)) {
    return `${String(value.length)} эл.`;
  }

  if (typeof value === 'boolean') {
    return value ? 'да' : 'нет';
  }

  return typeof value === 'number' ? String(value) : 'изменено';
}

/** Состояние сохранения черновика — рядом с заголовком, всегда на виду. */
function SaveState({
  status,
  dirty,
  savedAt,
  onRetry,
}: {
  status: SaveStatus;
  dirty: boolean;
  savedAt: Date | undefined;
  onRetry: () => void;
}) {
  if (status === 'saving') {
    return (
      <span className={styles['save']}>
        <LoaderCircle size={13} className={styles['save__spin']} /> Сохраняем…
      </span>
    );
  }

  if (status === 'error') {
    return (
      <span className={`${styles['save'] ?? ''} ${styles['save--error'] ?? ''}`}>
        <TriangleAlert size={13} /> Не сохранилось.
        <button type="button" className={styles['save__retry']} onClick={onRetry}>
          Повторить
        </button>
      </span>
    );
  }

  if (status === 'conflict') {
    return (
      <span className={`${styles['save'] ?? ''} ${styles['save--error'] ?? ''}`}>
        <TriangleAlert size={13} /> Запись изменили — автосохранение на паузе
      </span>
    );
  }

  if (dirty) {
    return <span className={styles['save']}>Есть правки — сохранятся сами</span>;
  }

  if (status === 'saved' && savedAt !== undefined) {
    return (
      <span className={styles['save']}>
        <Check size={13} /> Сохранено в {timeFormat.format(savedAt)}
      </span>
    );
  }

  return null;
}

/** Версия в истории: сравнить с тем, что в форме, и вернуть. */
function VersionRow({
  recordId,
  version,
  fields,
  current,
  disabled,
  onRevert,
}: {
  recordId: string;
  version: {
    id: string;
    number: number;
    author?: string | null;
    published: boolean;
    created_at: string;
  };
  fields: FormField[];
  current: Record<string, unknown>;
  disabled: boolean;
  onRevert: () => void;
}) {
  const [comparing, setComparing] = useState(false);

  const detail = useQuery({
    queryKey: ['version', version.id],
    enabled: comparing,
    staleTime: Infinity,
    queryFn: async () => {
      const response = await recordsVersion(recordId, version.id);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос версии');
      }

      return response.data;
    },
  });

  return (
    <li className={styles['version']}>
      <div className={styles['version__row']}>
        <div className={styles['version__main']}>
          <span className={styles['version__number']}>
            Версия {version.number}
            {version.published && (
              <Badge size="sm" tone="success">
                на сайте
              </Badge>
            )}
          </span>
          <span className={styles['muted']}>
            {dateFormat.format(new Date(version.created_at))}
            {version.author != null && ` · ${version.author}`}
          </span>
        </div>

        <div className={styles['version__actions']}>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            iconLeft={<GitCompare size={14} />}
            aria-pressed={comparing}
            onClick={() => {
              setComparing((value) => !value);
            }}
          >
            Сравнить
          </Button>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            iconLeft={<RotateCcw size={14} />}
            disabled={disabled}
            onClick={onRevert}
          >
            Вернуть
          </Button>
        </div>
      </div>

      {comparing &&
        (detail.data === undefined ? (
          <p className={styles['muted']}>Загружаем…</p>
        ) : (
          <DiffList
            fields={fields}
            before={detail.data.data ?? {}}
            after={current}
            beforeLabel={`в версии ${String(version.number)}`}
            afterLabel="сейчас"
          />
        ))}
    </li>
  );
}

/**
 * Разбирает ответ сервера о непринятых значениях в «ключ поля → что не так».
 *
 * Сервер присылает их в `errors` с местом вида `body.data.<ключ>`: экран
 * обязан подсветить конкретное поле, а не показать общий отказ поверх формы
 * из двадцати полей.
 */
function fieldErrors(error: ApiError): Record<string, string> {
  const result: Record<string, string> = {};

  for (const detail of error.details) {
    const location = detail.location ?? '';
    const key = location.startsWith('body.data.') ? location.slice('body.data.'.length) : '';

    if (key !== '' && detail.message != null) {
      result[key] = detail.message;
    }
  }

  return result;
}

/**
 * Форма перевода: только переводимые поля, а под каждым — значение на
 * основном языке, чтобы переводчику было с чем сверяться. Обязательности
 * у перевода нет: пропущенный перевод сайт заменит основным языком.
 */
function TranslationFields({
  fields,
  values,
  language,
  errors,
  siteID,
  onChange,
}: {
  fields: FormField[];
  values: Record<string, unknown>;
  language: string;
  errors: Record<string, string>;
  siteID: string;
  onChange: (key: string, value: unknown) => void;
}) {
  const translatable = fields.filter((field) => field.translatable);

  if (translatable.length === 0) {
    return (
      <p className={styles['muted']}>
        У этого типа нет переводимых полей. Отметьте в «Моделях» те, что отличаются на разных
        языках, — заголовок, описание.
      </p>
    );
  }

  const translations = translationsOf(values, language);

  return (
    <FormFields
      fields={translatable.map((field) => ({
        ...field,
        required: false,
        showIf: undefined,
        hint: originalHint(values[field.key]),
      }))}
      values={translations}
      errors={errors}
      path={`_i18n.${language}.`}
      siteID={siteID}
      onChange={onChange}
    />
  );
}

function translationsOf(
  values: Record<string, unknown>,
  language: string,
): Record<string, unknown> {
  const all = values['_i18n'];

  if (typeof all !== 'object' || all === null) {
    return {};
  }

  const one = (all as Record<string, unknown>)[language];

  return typeof one === 'object' && one !== null ? (one as Record<string, unknown>) : {};
}

function withTranslation(
  values: Record<string, unknown>,
  language: string,
  key: string,
  next: unknown,
): Record<string, unknown> {
  const all = (
    typeof values['_i18n'] === 'object' && values['_i18n'] !== null ? values['_i18n'] : {}
  ) as Record<string, unknown>;

  return {
    ...values,
    _i18n: { ...all, [language]: { ...translationsOf(values, language), [key]: next } },
  };
}

/** Подсказка с основным языком: текст без разметки, коротко. */
function originalHint(value: unknown): string {
  if (typeof value !== 'string' || value.trim() === '') {
    return 'На основном языке пусто.';
  }

  const text = value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return `Основной язык: «${text.length > 120 ? `${text.slice(0, 120)}…` : text}»`;
}

function seoOf(values: Record<string, unknown>): Record<string, unknown> {
  const raw = values['_seo'];

  return typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
}
